import * as DV from "@impero-com/common/utils/dataView";
import { ExportableArray } from "@impero-com/common/utils/export";
import { assertNever } from "@impero-com/common/utils/obj";
import i18next from "i18next";

import { DetailedUserOverviewDtoContent } from "api/generated/model";
import { userRoles } from "org/user/roles";

import { USER_EXTRACTOR_KEYS } from "./extractorKeys";

import {
    getLoginWithPassword,
    INVITE_PENDING,
    NOT_INVITED,
    ALLOWED,
    NOT_ALLOWED,
    shouldDisplayRoles,
} from "./index";

export function exportUserList(
    dataView: DV.DataView<DetailedUserOverviewDtoContent>,
    selectedIds: Array<string>,
    controlTestingEnabled: boolean,
    controlEntitiesEnabled: boolean,
    mcpEnabled: boolean,
    scimEnabled: boolean
): ExportableArray {
    const selectedIdsSet = new Set(selectedIds);

    const extractorName = DV.getExtractor(dataView, USER_EXTRACTOR_KEYS.NAME);
    const extractorEmail = DV.getExtractor(dataView, USER_EXTRACTOR_KEYS.EMAIL);
    const extractorGroups = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.GROUPS
    );
    const extractorDirectControlAssignments = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS
    );
    const extractorPrograms = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.PROGRAMS
    );
    const extractorPendingActivities = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES
    );
    function getControlTestingColumnExtractors() {
        if (!controlTestingEnabled) {
            return null;
        }

        return {
            testingControlAssignments: DV.getExtractor(
                dataView,
                USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS
            ),
            testingPrograms: DV.getExtractor(
                dataView,
                USER_EXTRACTOR_KEYS.TESTING_PROGRAMS
            ),
            pendingTestingActivities: DV.getExtractor(
                dataView,
                USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES
            ),
        };
    }

    const extractorReports = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.REPORTS
    );
    const extractorRisks = DV.getExtractor(
        dataView,
        USER_EXTRACTOR_KEYS.RISK_MANAGEMENT
    );

    const headerLabels = [
        "Id",
        "Name",
        "Email",
        "Login with password: Not allowed",
        "Login with password: Not invited",
        "Login with password: Invite pending",
        "Login with password: Allowed",
        "Groups",
        "Direct control assignments",
        "Programs",
        "Pending activities",
        ...(controlTestingEnabled
            ? ["Direct test assignments", "Testing programs", "Test activities"]
            : []),
        "Reports",
        "Risk management",
        ...(scimEnabled ? ["User Status"] : []),
    ];

    const displayRoles = shouldDisplayRoles(dataView);
    const allRoles = userRoles({
        controlTestingEnabled,
        controlEntitiesEnabled,
        mcpEnabled,
    });
    if (displayRoles) {
        const en = i18next.getFixedT("en");
        for (const role of allRoles) {
            headerLabels.push("Role: " + en("User_Roles_" + role));
        }
    }

    const table: Array<Array<string | undefined>> = [];
    DV.forEach(dataView.dataSource.iter(), (userOverview): void => {
        if (selectedIdsSet.has(dataView.dataSource.getId(userOverview))) {
            const row: Array<string | undefined> = [];

            row.push(
                userOverview.resource.user.id.toString(),
                extractorName.asString(userOverview),
                extractorEmail.asString(userOverview)
            );

            const pwLogin = getLoginWithPassword(
                userOverview.resource.authenticationStatus
            );
            switch (pwLogin) {
                case INVITE_PENDING:
                    row.push("N", "N", "Y", "N");
                    break;
                case NOT_INVITED:
                    row.push("N", "Y", "N", "N");
                    break;
                case ALLOWED:
                    row.push("N", "N", "N", "Y");
                    break;
                case NOT_ALLOWED:
                    row.push("Y", "N", "N", "N");
                    break;
                default:
                    return assertNever(pwLogin);
            }

            const controlTestingExtractors =
                getControlTestingColumnExtractors();
            const controlTestingValues = controlTestingExtractors
                ? Object.values(controlTestingExtractors).map((extractor) =>
                      extractor.asString(userOverview)
                  )
                : [];

            row.push(
                ...[
                    extractorGroups.asString(userOverview),
                    extractorDirectControlAssignments.asString(userOverview),
                    extractorPrograms.asString(userOverview),
                    extractorPendingActivities.asString(userOverview),
                    ...controlTestingValues,
                    extractorReports.asString(userOverview),
                    extractorRisks.asString(userOverview),
                ]
            );

            if (scimEnabled) {
                const extractorStatus = DV.getExtractor(
                    dataView,
                    USER_EXTRACTOR_KEYS.PROVISION_STATUS
                );
                row.push(extractorStatus.asString(userOverview));
            }

            if (displayRoles) {
                const fmtBool = (b: boolean) => (b ? "Y" : "N");
                const roles = userOverview.resource.roles!;
                for (const role of allRoles) {
                    row.push(fmtBool(roles.includes(role)));
                }
            }

            table.push(row);
        }
    });

    return {
        header: headerLabels,
        content: table,
    };
}
